import { useCallback, useEffect, useState } from 'react';
import { PageHeader, EmptyState, TableSkeleton } from '../components/common/PageHeader';
import { Modal, DeleteConfirmModal } from '../components/common/Modal';
import CouponForm, {
  emptyCouponForm,
  couponToForm,
  formToCouponPayload,
} from '../components/forms/CouponForm';
import {
  listCoupons,
  createCoupon,
  updateCoupon,
  deleteCoupon,
} from '../services/couponService';
import { getErrorMessage } from '../services/apiClient';
import { useToast } from '../context/ToastContext';
import { formatDate } from '../utils/format';

export default function CouponsPage() {
  const { showToast } = useToast();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deletingItem, setDeletingItem] = useState(null);
  const [form, setForm] = useState(emptyCouponForm);

  const fetchCoupons = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listCoupons({ limit: 100 });
      setCoupons(res.data || []);
    } catch (err) {
      showToast(getErrorMessage(err), 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchCoupons();
  }, [fetchCoupons]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyCouponForm);
    setModalOpen(true);
  };

  const openEdit = (coupon) => {
    setEditing(coupon);
    setForm(couponToForm(coupon));
    setModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.code || form.discountValue === '') {
      showToast('Code and discount value are required', 'error');
      return;
    }
    setSaving(true);
    try {
      const payload = formToCouponPayload(form);
      if (editing) {
        await updateCoupon(editing._id, payload);
        showToast('Coupon updated successfully');
      } else {
        await createCoupon(payload);
        showToast('Coupon created successfully');
      }
      setModalOpen(false);
      fetchCoupons();
    } catch (err) {
      showToast(getErrorMessage(err), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingItem) return;
    setDeleting(true);
    try {
      await deleteCoupon(deletingItem._id);
      showToast('Coupon deleted successfully');
      setDeleteOpen(false);
      fetchCoupons();
    } catch (err) {
      showToast(getErrorMessage(err), 'error');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Coupons"
        description="Manage discount codes for checkout"
        action={
          <button type="button" className="btn btn-primary" onClick={openCreate}>
            + Add Coupon
          </button>
        }
      />

      <div className="card">
        {loading ? (
          <TableSkeleton rows={5} cols={6} />
        ) : coupons.length === 0 ? (
          <EmptyState message="No coupons yet" />
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Discount</th>
                  <th>Min Order</th>
                  <th>Usage</th>
                  <th>Expires</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {coupons.map((coupon) => (
                  <tr key={coupon._id}>
                    <td>
                      <strong>{coupon.code}</strong>
                    </td>
                    <td>
                      {coupon.discountType === 'percentage'
                        ? `${coupon.discountValue}%`
                        : `₹${coupon.discountValue}`}
                      {coupon.maxDiscountAmount
                        ? ` (max ₹${coupon.maxDiscountAmount})`
                        : ''}
                    </td>
                    <td>₹{coupon.minOrderValue || 0}</td>
                    <td>
                      {coupon.usedCount || 0}
                      {coupon.usageLimit ? ` / ${coupon.usageLimit}` : ''}
                    </td>
                    <td>{coupon.expiresAt ? formatDate(coupon.expiresAt) : '—'}</td>
                    <td>
                      <span
                        className={`badge ${coupon.isActive ? 'badge-success' : 'badge-muted'}`}
                      >
                        {coupon.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-sm btn-ghost"
                        onClick={() => openEdit(coupon)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={() => {
                          setDeletingItem(coupon);
                          setDeleteOpen(true);
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        open={modalOpen}
        title={editing ? 'Edit Coupon' : 'Add Coupon'}
        onClose={() => setModalOpen(false)}
      >
        <form onSubmit={handleSave}>
          <CouponForm form={form} onChange={setForm} />
          <div className="modal-actions" style={{ marginTop: '1.5rem' }}>
            <button type="button" className="btn btn-ghost" onClick={() => setModalOpen(false)}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </Modal>

      <DeleteConfirmModal
        open={deleteOpen}
        title="Delete Coupon"
        message={`Delete coupon "${deletingItem?.code}"?`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteOpen(false)}
        loading={deleting}
      />
    </div>
  );
}
